import { Injectable } from '@angular/core';
import { NotificationService } from 'src/app/shared/notification.service';
import { AuthService } from 'src/app/services/auth.service';
import { combineLatest, Observable, of, timer } from 'rxjs';
import { catchError, debounceTime, map, shareReplay, switchMap } from 'rxjs/operators';
import moment from  'moment-timezone/builds/moment-timezone-with-data-1970-2030.min.js';
import { AngularFirestore } from "@angular/fire/compat/firestore";
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { where, query, average, collection, getAggregateFromServer, getCountFromServer, count, sum, getDoc, getDocs, updateDoc, arrayUnion, doc } from '@angular/fire/firestore';
import { environment } from 'src/environments/environment';

// Order ids used to be the ordering app's Date.now() (13 digits), and these
// queries read time straight off them. Since 2026-09 the backend allocates
// short ids (8+ digits, services/orderIdService.js in friggkantine-
// cloudfunctions) that carry no time, so time comes from `createdAt`, which
// every order has had since 2024-11-19. Older orders have no createdAt but a
// 13-digit timestamp id, so date ranges that reach back before that date also
// run the old id-range query (kept to 13-digit ids) and merge the two.
const CREATED_AT_SINCE_SECONDS = 1731974400; // 2024-11-19T00:00:00Z
const isLegacyTimestampId = (id: string) => /^\d{13}$/.test(String(id));
// ms since epoch for sorting: createdAt, else the legacy timestamp id.
const orderTimeMs = (data: any, id?: string): number => {
  const createdAt = data && data['createdAt'];
  if (createdAt && typeof createdAt.toMillis === 'function') return createdAt.toMillis();
  const legacyId = String(id ?? data?.['orderId'] ?? '');
  return isLegacyTimestampId(legacyId) ? Number(legacyId) : 0;
};

type OrderDocs = { docs: { id: string, data: () => unknown }[] };

// Newest first, one entry per doc id, at most `max` — the shape the order
// report expects from a snapshot.
const mergeOrderDocs = (snapshots: OrderDocs[], max: number): OrderDocs => {
  const byId = new Map<string, OrderDocs['docs'][number]>();
  snapshots.forEach(sp => sp.docs.forEach(d => byId.set(d.id, d)));
  const docs = [...byId.values()]
    .sort((a, b) => orderTimeMs(b.data(), b.id) - orderTimeMs(a.data(), a.id))
    .slice(0, max);
  return { docs };
};

// "Visa •••• 4242 (Apple Pay)" for an order's stored card, '' without one.
const WALLET_NAMES: { [w: string]: string } = { apple_pay: 'Apple Pay', google_pay: 'Google Pay', samsung_pay: 'Samsung Pay' };
export const cardLabel = (order: any): string => {
  if (!order || !order.cardLast4) return '';
  const brand = order.cardBrand ? order.cardBrand.charAt(0).toUpperCase() + order.cardBrand.slice(1) : 'Card';
  const wallet = order.cardWallet ? ` (${WALLET_NAMES[order.cardWallet] || order.cardWallet})` : '';
  return `${brand} •••• ${order.cardLast4}${wallet}`;
};

// Order time in ms: createdAt, else the legacy 13-digit id, else orderDate.
export const orderTimeOf = (order: any): number => {
  const c = order && order.createdAt;
  if (c && typeof c.toMillis === 'function') return c.toMillis();
  if (c && typeof c.seconds === 'number') return c.seconds * 1000;
  if (/^\d{13}$/.test(String(order && order.orderId))) return Number(order.orderId);
  return order && order.orderDate ? moment(order.orderDate).valueOf() : 0;
};

@Injectable({
  providedIn: 'root'
})
export class OrdersService {

  constructor(public afs: AngularFirestore, private notifierService: NotificationService, private authService: AuthService, private afAuth: AngularFireAuth, private http: HttpClient) {

  }

  addCompany(object) {
    object.updatedOn = moment().unix();
    object.createdOn = moment().unix();
    this.afs.collection('company')
      .add(object).then(data => {
        this.notifierService.showNotification('success', "Successfully added");
      })
      .catch(err => {
        this.notifierService.showNotification('error', "Some issue in adding");
      })
  }


  // How long an order keeps a live listener after it's placed. Past that,
  // it's treated as settled and only refreshed by the periodic static
  // re-fetch below — an order rarely changes 20+ minutes in, so it doesn't
  // need to be part of the realtime-watched window (which otherwise grows
  // unbounded and re-decodes all 200 rows on every metadata-only Firestore
  // event; see the perf investigation on this component).
  private readonly ORDERS_LIVE_WINDOW_MINUTES = 20;
  private readonly ORDERS_LIVE_WINDOW_REFRESH_MS = 60 * 1000;
  private readonly ORDERS_STATIC_REFRESH_MS = 5 * 60 * 1000;

  private mapOrderDoc(data: object) {
    return {
      ...data,
      orderNo: "..." + String(data['orderNo'] ?? data['orderId'] ?? '').slice(-4),
      companyId: data['companyId'],
      name: data['name'].replace(/_/g, ' '),
      orderDate: moment(data['orderDate']).format("HH:mm:ss"),
      user: data['user'] ? data['user']['name'] : "Unknown"
    }
  }

  // A settled payment on a finished order won't be written to again, so it
  // doesn't need to be treated as "live" even while it's still inside the
  // rolling time window below.
  private readonly SETTLED_PAYMENT_STATUSES = ['Captured', 'Partial Refunded', 'Fully Refunded'];
  private isOrderSettled(data: object) {
    return this.SETTLED_PAYMENT_STATUSES.includes(data['paymentStatus']) && data['status'] === 'Finished';
  }

  getOrders$() {
    // snapshotChanges() re-emits the full current result array on every
    // change, even for docs that didn't themselves change — so without this,
    // every order in the live window gets re-decoded as a bystander whenever
    // any *one* of them updates. AngularFire keeps the same doc-snapshot
    // object reference for entries that weren't touched by a given event, so
    // caching by that reference lets us skip re-decoding those bystanders.
    const decodedCache = new Map<string, { ref: object, mapped: any }>();
    const decodeCached = (docSnap: any) => {
      const cached = decodedCache.get(docSnap.id);
      if (cached && cached.ref === docSnap) return cached.mapped;
      const mapped = this.mapOrderDoc(docSnap.data() as object);
      decodedCache.set(docSnap.id, { ref: docSnap, mapped });
      return mapped;
    };

    // Live-updated: only orders placed within the last N minutes. The cutoff
    // is recomputed on a rolling basis so this window (and the listener's
    // result set) stays bounded as the session stays open.
    const recentLive$ = timer(0, this.ORDERS_LIVE_WINDOW_REFRESH_MS).pipe(
      switchMap(() => {
        const cutoff = moment().subtract(this.ORDERS_LIVE_WINDOW_MINUTES, 'minutes').toDate();
        return this.afs.collection('orders', ref =>
          ref.where('createdAt', '>=', cutoff).orderBy('createdAt', 'desc')
        ).snapshotChanges();
      }),
      debounceTime(300),
      map(changes => changes.map(obj => decodeCached(obj.payload.doc)))
    );

    // Not live: a periodic (not per-event) snapshot of the last 200 orders,
    // covering settled orders the live window above has aged out of.
    const staticRecent$ = timer(0, this.ORDERS_STATIC_REFRESH_MS).pipe(
      switchMap(() => this.afs.collection('orders', ref =>
        ref.orderBy('createdAt', 'desc').limit(200)
      ).get()),
      map(snap => snap.docs.map(d => decodeCached(d)))
    );

    return combineLatest([recentLive$, staticRecent$]).pipe(
      map(([recent, older]) => {
        const merged = new Map<string, any>();
        older.forEach(o => merged.set(o['orderId'], { ...o, live: false }));
        recent.forEach(o => merged.set(o['orderId'], { ...o, live: !this.isOrderSettled(o) })); // live data wins on overlap
        return Array.from(merged.values())
          .sort((a, b) => orderTimeMs(b) - orderTimeMs(a))
          .slice(0, 200);
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    )
  }

  getTotalOrders$() {
    return new Promise((resolve, reject) => {

      const startOfDay = moment().tz("Europe/Oslo").startOf('day').toDate();
      const transactionCollection = collection(this.afs.firestore, 'orders')// collection of users


      getAggregateFromServer(query(transactionCollection, where('createdAt', '>', startOfDay)), {
        count: count(),
        // total: sum('price')
      }).then((result) => {
        console.log(result.data())
        resolve(result.data())
      }).catch((error) => {
        console.log(error)
        reject(error)
      }
      )
    })
  }

  getMissingPaymentOrders$(status) {
    return new Promise((resolve, reject) => {

      const startOfDay = moment().tz("Europe/Oslo").startOf('day').toDate();
      const transactionCollection = collection(this.afs.firestore, 'orders')// collection of users


      getAggregateFromServer(query(transactionCollection, where('createdAt', '>', startOfDay), where('status', '==', status)), {
        count: count()
      }).then((result) => {
        console.log(result.data())
        resolve(result.data().count)
      }).catch((error) => {
        console.log(error)
        reject(error)
      }
      )
    })
  }
  // dateFrom/dateTo are unix seconds. Returns { get() } like a collection
  // reference, so the order report's `.get().subscribe(...)` is unchanged.
  getOrdersByTime$(dateFrom, dateTo, maxOrders: number = 200) {
    return this.byTimeRange(dateFrom, dateTo, maxOrders, ref => ref);
  }

  private byTimeRange(dateFrom, dateTo, maxOrders: number, scope: (ref: any) => any) {
    const from = Number(dateFrom), to = Number(dateTo);
    // The old id-as-time range, kept to real 13-digit timestamp ids (a short
    // new id can sort inside a string range by accident).
    const legacy = () => (this.afs.collection('orders', ref => scope(ref)
      .where('orderId', '>=', from.toString())
      .where('orderId', '<=', to.toString())
      .orderBy('orderId', 'desc').limit(maxOrders)).get() as Observable<OrderDocs>).pipe(
        map(sp => ({ docs: sp.docs.filter(d => isLegacyTimestampId(d.id)) })));
    return {
      get: (): Observable<OrderDocs> => {
        const recent$ = (this.afs.collection('orders', ref => scope(ref)
          .where('createdAt', '>=', new Date(from * 1000))
          .where('createdAt', '<=', new Date(to * 1000 + 999))
          .orderBy('createdAt', 'desc').limit(maxOrders)).get() as Observable<OrderDocs>).pipe(
            // Scoped to a system this needs the systemId + createdAt index;
            // until it exists, answer as before (id range only).
            catchError(err => {
              console.error('Orders by time: createdAt query failed (composite index missing?)', err);
              return legacy();
            }));
        if (from >= CREATED_AT_SINCE_SECONDS) return recent$;
        // Reaches back before createdAt existed: merge in the old range.
        return combineLatest([recent$, legacy()]).pipe(map(snaps => mergeOrderDocs(snaps, maxOrders)));
      }
    };
  }

  // No date bound: used when the time-period filter is cleared, so a search
  // can match orders from any day instead of just the default today window.
  getRecentOrders$(maxOrders: number = 200) {
    return this.afs.collection('orders', ref => ref.orderBy('createdAt', 'desc').limit(maxOrders))
  }

  // Scoped to one system, no date bound - getRecentOrders$ above only pulls
  // the maxOrders most recent orders across every system, so a busy network
  // can fill that window with today's orders alone and hide an older order
  // that belongs to the requested system. Querying by name directly avoids
  // that. Requires a Firestore composite index on (name, orderId).
  // Matches on systemId rather than name (names can differ in spelling
  // between orders and system_info). `systemIds` holds every id the system
  // is known by - the system_info doc id and its own systemId field - since
  // it's not consistent which one an order stores. Requires a composite
  // index on (systemId, orderId).
  // Newest by createdAt (composite index systemId + createdAt) merged with the
  // old id-ordered query, which still finds orders from before createdAt. If
  // the createdAt index is missing it falls back to the old query alone.
  getOrdersBySystem$(systemIds: string[], maxOrders: number = 200) {
    return {
      get: (): Observable<OrderDocs> => {
        const byTime$ = (this.afs.collection('orders', ref => ref.where('systemId', 'in', systemIds)
          .orderBy('createdAt', 'desc').limit(maxOrders)).get() as Observable<OrderDocs>).pipe(
            catchError(err => {
              console.error('Orders by system: createdAt query failed (index systemId+createdAt missing?)', err);
              return of({ docs: [] } as OrderDocs);
            }));
        const byId$ = this.afs.collection('orders', ref => ref.where('systemId', 'in', systemIds)
          .orderBy('orderId', 'desc').limit(maxOrders)).get() as Observable<OrderDocs>;
        return combineLatest([byTime$, byId$]).pipe(map(snaps => mergeOrderDocs(snaps, maxOrders)));
      }
    };
  }

  // Scoped to one system AND a date range - getOrdersByTime$ alone pulls
  // maxOrders worth of every system's orders in that range, so a busy
  // network can fill the limit with other systems before this one's orders
  // even get read. Filtering by name server-side avoids that. Uses the same
  // (name, orderId) composite index as getOrdersBySystem$ - the orderId
  // range here doesn't need its own index, since a composite index already
  // supports range scans on its last field.
  getOrdersByTimeAndSystem$(dateFrom, dateTo, systemIds: string[], maxOrders: number = 200) {
    return this.byTimeRange(dateFrom, dateTo, maxOrders, ref => ref.where('systemId', 'in', systemIds));
  }

  // Exact match only, no date bound - lets a known order number be found
  // regardless of how old it is or how many orders exist in between, and
  // regardless of the currently selected time period. Tries orderNo as
  // both a string and a number (Firestore equality is type-sensitive and
  // it's not certain from here which type the field is actually stored
  // as), plus orderId. None of these need a composite index - single-field
  // equality is covered by Firestore's automatic per-field index.
  async findOrderByNumber$(orderNo: string) {
    const ordersCollection = collection(this.afs.firestore, 'orders');
    const lookups = [
      getDocs(query(ordersCollection, where('orderNo', '==', orderNo))),
      getDocs(query(ordersCollection, where('orderId', '==', orderNo)))
    ];
    const numericOrderNo = Number(orderNo);
    if (orderNo.trim() !== '' && !isNaN(numericOrderNo)) {
      lookups.push(getDocs(query(ordersCollection, where('orderNo', '==', numericOrderNo))));
    }

    const results = await Promise.all(lookups.map(lookup => lookup.catch(error => {
      console.error('Order number lookup failed:', error);
      return null;
    })));

    const merged = new Map<string, object>();
    results.forEach(snapshot => {
      if (!snapshot) return;
      snapshot.docs.forEach(d => merged.set(d.id, d.data()));
    });
    return Array.from(merged.values());
  }


  // Orders paid with a card whose last four digits are these (Stripe: web and
  // kiosk tablet), newest first. Served by the backend (GET
  // /orderCards/search), which also covers orders paid before the card was
  // stored on the order; the caller narrows by date and system.
  findOrdersByCardLast4$(last4: string) {
    return this.cardSearch({ last4 });
  }

  // Every order paid with the very same card (Stripe's card fingerprint) —
  // not merely the same last four digits.
  findOrdersByCardFingerprint$(fingerprint: string) {
    return this.cardSearch({ fingerprint });
  }

  private async cardSearch(params: { [k: string]: string }): Promise<any[]> {
    const token = await this.authService.getToken();
    const res: any = await this.http.get(environment.apiUrl + '/widgets/orderCards/search', {
      params,
      headers: new HttpHeaders({ 'Authorization': 'Bearer ' + token })
    }).toPromise();
    return (res && res.orders) || [];
  }

  async refundPayment(orderId: string, reason: string, paymentType: 'stripe' | 'vipps' | 'credit', selectedProducts: any[]) {
    try {
      const user = await this.afAuth.currentUser;
      if (!user) {
        this.notifierService.showNotification('error', 'You must be signed in to process a refund.');
        return;
      }

      const orderDocRef = doc(this.afs.firestore, 'orders', orderId);
      const orderSnap = await getDoc(orderDocRef);
      const orderData = orderSnap.exists() ? orderSnap.data() : null;

      if (!orderData) {
        this.notifierService.showNotification('error', 'Order not found.');
        return;
      }

      const paymentStatus = orderData?.['paymentStatus']?.toLowerCase() || '';
      const isFullyRefunded = paymentStatus === 'refunded';

      if (isFullyRefunded) {
        this.notifierService.showNotification('info', 'Refund already processed for this order.');
        return;
      }

      const employeeEmail = user.email || 'unknown';
      const idToken = await user.getIdToken();
      const apiUrl = `${environment.apiUrl}/widgets/adminRefund/${paymentType}/${orderId}`;

      //  Send selected products as JSON payload
      const result = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason, selectedProducts })
      });

      if (!result.ok) throw new Error('Refund API failed');

      const supportEntry = {
        message: reason,
        email: employeeEmail,
        date: new Date(),
        products: selectedProducts
      };

      await updateDoc(orderDocRef, {
        supportMessages: arrayUnion(supportEntry)
      });

      this.notifierService.showNotification('success', 'Refund processed successfully.');
    } catch (err) {
      console.error('Refund failed:', err);
      this.notifierService.showNotification('error', 'Refund failed. Please try again.');
    }
  }

getOrderDetail(orderId: string) {
  return this.afs.collection('orderDetail', ref => ref.where('orderId', '==', orderId))
    .snapshotChanges()
    .pipe(
      map(snapshot => {
        return snapshot.map(doc => {
          const data = doc.payload.doc.data() as object || {}; // ensure it's an object
          return {
            id: doc.payload.doc.id,
            ...data
          };
        });
      })
    );
}



}
